import React, { useState, useEffect } from "react";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { TableFooter, Button } from "@mui/material";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMField } from "../../utils/PMField";
import { TrashIcon } from "@heroicons/react/24/outline";
import ProConfirmation from "../../utils/ProConfirmation";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { projectParticipantsRole, ProjectParticipantStatus } from "./utils";
import PMListbox from "../../utils/PMListbox";
import { useUserHasAccess } from "../../../routes/roleOnly";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function ParticipantsSection(props) {
  const {
    setFieldValue,
    values,
    isLoading,
    drag,
    project,
    title,
    name,
    showAdmin = true,
    roles = projectParticipantsRole,
  } = props;

  const isAdmin = useUserHasAccess({ role: "project", action: "manage" });

  const rows = values[name];

  const columns = [
    { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
    { id: "user", label: "Bruker", minWidth: 300, active: true },
    {
      id: "role",
      label: "Role",
      minWidth: 20,
      align: "left",
      active: true,
    },
    {
      id: "admin",
      label: "Admin",
      minWidth: 20,
      align: "left",
      active: showAdmin,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 20,
      align: "center",
      active: true,
    },

    {
      id: "actions",
      label: "",
      minWidth: 50,
      align: "left",
      active: true,
    },
  ];

  const orderItems = (rows) => {
    var array = [];

    if (!rows) return;

    let orderIndex = 0;
    rows.map((row, i) => {
      const rowParams = {
        ...row,
        row_order: row.delete ? null : orderIndex++, // Only increment order for non-deleted items
        draggableId: "id" + row.id,
      };
      array.push(rowParams);
    });
    return array;
  };

  // Title column is only used when group_row is true
  const defaultRows = [
    {
      id: null,
      activity_id: null,
      row_order: null,
      title: "",
    },
  ];

  const [items, setItems] = useState(orderItems(rows ? rows : defaultRows));

  useEffect(() => {
    setFieldValue(name, items);
  }, [items]);

  const addItemRow = () => {
    // Calculate the next row_order based on non-deleted items
    const nextOrder = items.filter((item) => !item.delete).length;

    const newRow = {
      id: null,
      title: "",
      row_order: nextOrder,
      draggableId: "temp-" + items.length + "-" + Date.now(),
    };
    setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    // Find the item by its unique identifier (id or draggableId) instead of row_order
    const itemIndex = prevItems.findIndex(
      (item) => item.id === data.id || item.draggableId === data.draggableId
    );

    if (itemIndex !== -1) {
      let item = {
        ...prevItems[itemIndex],
        [e.target.name]: e.target.value,
      };
      prevItems[itemIndex] = item;
      setItems(prevItems);
    }
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    // Get the actual source index in the full items array
    const sourceIndex = items.findIndex(
      (item) => item.draggableId === result.draggableId
    );

    // Find the actual destination index in the full items array
    let actualDestinationIndex = 0;
    let displayIndex = 0;

    for (let i = 0; i < items.length; i++) {
      if (!items[i].delete) {
        if (displayIndex === result.destination.index) {
          actualDestinationIndex = i;
          break;
        }
        displayIndex++;
      }
    }

    const newItems = reorder(items, sourceIndex, actualDestinationIndex);

    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    // Mark selectedItem as deleted instead of removing from array
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow ? { ...item, delete: true } : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (id) => {
    setSelectedItemRow(id);
    setDeleteConfirmationOpen(true);
  };

  // Filter out deleted items for display only
  const displayItems = items?.filter((item) => !item.delete) || [];

  const itemRow = (item, provided, snapshot) => {
    return (
      <TableRow
        hover
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        {drag ? (
          <TableCell>
            <DragHandleIcon />
          </TableCell>
        ) : null}

        <TableCell align="left">
          <div className="">
            <EmployeeSelector
              noLabel={false}
              selected={item?.user}
              name="user_id"
              onChange={(e) => onItemRowChange(e, item)}
            />
          </div>
        </TableCell>
        <TableCell align="left">
          <div>
            <PMListbox
              noLabel
              name="role"
              label="Rolle"
              options={roles}
              selected={item?.role}
              onChange={(e) => {
                onItemRowChange(
                  {
                    target: {
                      name: "role",
                      value: e.value,
                    },
                  },
                  item
                );
              }}
            />
          </div>
        </TableCell>
        {showAdmin && (
          <TableCell align="left">
            <div className="px-5 pb-2">
              <PMField
                type="checkbox"
                fullWidth
                name="admin"
                checked={item.admin}
                onChange={(e) =>
                  onItemRowChange(
                    {
                      target: {
                        name: e.target.name,
                        value: e.target.checked,
                      },
                    },
                    item
                  )
                }
              />
            </div>
          </TableCell>
        )}

        <TableCell align="left">
          <div className="px-5 pb-1">
            <ProjectParticipantStatus participant={item} />
          </div>
        </TableCell>

        {props.actions && (
          <TableCell align="right">
            <TrashIcon
              className="cursor-pointer h-5 h-5 text-red-400"
              onClick={() => deleteRow(item.id)}
            />
          </TableCell>
        )}
      </TableRow>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      <table className="min-w-full w-full">
        <TableHead>
          {/* <TableRow>
                        <TableCell></TableCell>
                        <TableCell align="center" colSpan={2}>Product</TableCell>
                        { props.purchase_price ? 
                            <TableCell align="center" colSpan={3}>Innkjøpspris</TableCell>
                        : null }

                        { props.sales_price ? 
                            <TableCell align="center" colSpan={4}>Salgspris</TableCell>
                        : null }
                    </TableRow> */}
          <TableRow>
            {columns.map((column) =>
              column.active ? (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    top: 57,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ) : null
            )}
          </TableRow>
        </TableHead>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {displayItems?.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        <TableFooter>
          <TableRow>
            <TableCell align="left" colSpan={2}>
              <Button onClick={() => addItemRow()}>Ny rad</Button>
            </TableCell>

            <TableCell laft="left" colSpan={3}></TableCell>
          </TableRow>
        </TableFooter>
      </table>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        // item_id={confirmation.item_id}
        yesCallback={handleDeleteRow}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

ParticipantsSection.defaultProps = {
  budget_hours: false,
  budget_fee_rate: false,
  budget_fee: false,
  number: false,
  invoiceable: false,
  inactive: false,
  actions: true,
  name: "participants",
  drag: true,
  title: true,
  module: null,
  showAdmin: true,
  roles: projectParticipantsRole,
};
